import { usePrefs, useFlags, useRequestData } from 'wsm-common-data';
import { getPageSize } from '../utils';
import { useCardsPerRow } from './useCardsPerRow';

/**
 * Derives the desktop Standard Horizontal layout from the pageSize and
 * useCarousel prefs. Both only apply behind the enable-rec-vehicles-enhancements
 * flag, on desktop, in the Standard Horizontal style, so isConfigurableLayout
 * gates the whole block and every other layout keeps the original rendering.
 *
 * @param {object} params
 * @param {boolean} params.isSingleRow - Standard Horizontal, outside the nav drawer
 * @param {boolean} params.isLoading - whether the vehicle data is still loading
 * @param {Array} params.vehicleData - untrimmed vehicle list from the API
 * @returns {{
 *   isConfigurableLayout: boolean,
 *   pageSize: number,
 *   useCarousel: boolean,
 *   isMultiRow: boolean,
 *   cardsPerRow: number,
 *   displayedVehicles: Array,
 *   showCarousel: boolean
 * }}
 */
export const useConfigurableLayout = ({
	isSingleRow,
	isLoading,
	vehicleData
}) => {
	const { pageSize: pageSizePref, useCarousel: useCarouselPref } = usePrefs();
	const flags = useFlags();
	const { deviceType } = useRequestData();

	const isConfigurableLayout =
		Boolean(flags['enable-rec-vehicles-enhancements']) &&
		deviceType === 'DESKTOP' &&
		isSingleRow;
	const pageSize = getPageSize(pageSizePref);
	const useCarousel = useCarouselPref !== 'false';
	const isMultiRow = isConfigurableLayout && !useCarousel;
	const cardsPerRow = useCardsPerRow(isConfigurableLayout && useCarousel);
	// Trim to pageSize: the fetch asks for extra vehicles to cover ones dropped
	// for missing images, so vehicleData is usually longer than pageSize.
	const displayedVehicles = isConfigurableLayout
		? vehicleData.slice(0, pageSize)
		: vehicleData;
	// No controls when every card already fits in the row.
	const showCarousel =
		isConfigurableLayout &&
		useCarousel &&
		!isLoading &&
		displayedVehicles.length > cardsPerRow;

	return {
		isConfigurableLayout,
		pageSize,
		useCarousel,
		isMultiRow,
		cardsPerRow,
		displayedVehicles,
		showCarousel
	};
};
